import React, { useState, useEffect, useRef } from "react";
import Header from "./Components/Header";
import Banner from "./Components/Banner";
import DocsAI from "./Components/Products/DocsAI/DocsAI";
import InsightsAI from "./Components/Products/InsightsAI/InsightsAI";
import EngageAI from "./Components/Products/EngageAI/EngageAI";
import Footer from "./Components/Footer";
import WhatsApp from "./Components/Products/WhatsApp/WhatsApp";
import Assets from "./assets/Assets";
import { Drawer } from "antd";

// Content Imports for Feature Availability Auditing (Exact disk casing)
import docsAIContent from "./Components/Products/DocsAI/docsAIContent";
import insightsAIContent from "./Components/Products/InsightsAI/InsightsAIContent";
import engageAIContent from "./Components/Products/EngageAI/EngageAIContent";
import whatsAppContent from "./Components/Products/WhatsApp/WhatsAppContent";

import { Brain, FileText, TrendingUp, Briefcase, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "./Styles/Main.scss";

const WhatsAppIcon = ({ size = 16, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={`partner-apple-tab-icon ${className}`}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.572-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c-.001 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const productTabs = [
  { name: "EngageAI", icon: Brain },
  { name: "DocsAI", icon: FileText },
  { name: "InsightsAI", icon: TrendingUp },
  { name: "WhatsApp", icon: WhatsAppIcon },
];

const sectionTabs = [
  "Overview",
  "Demos",
  "Pricing",
  "Use Cases",
  "Customer Stories",
  "Resources",
];

const sectionIdMap = {
  "Overview": "section-overview",
  "Demos": "section-demos",
  "Pricing": "section-pricing",
  "Use Cases": "section-usecases",
  "Customer Stories": "section-customer-stories",
  "Resources": "section-resources",
};

const productPathMap = {
  docsai: "DocsAI",
  insightsai: "InsightsAI",
  engageai: "EngageAI",
  whatsapp: "WhatsApp",
};

// Helper: Audit whether a product has valid content for a section
const checkSectionAvailable = (productName, secName) => {
  let content = null;
  switch (productName) {
    case "DocsAI":
      content = docsAIContent;
      break;
    case "InsightsAI":
      content = insightsAIContent;
      break;
    case "EngageAI":
      content = engageAIContent;
      break;
    case "WhatsApp":
      content = whatsAppContent;
      break;
    default:
      content = engageAIContent;
  }

  if (!content) return false;

  switch (secName) {
    case "Overview":
      return Boolean(content.overview);
    case "Demos":
      return Boolean((content.demos || content.demo)?.length > 0);
    case "Pricing":
      return Boolean(content.pricing?.plans && content.pricing.plans.length > 0);
    case "Use Cases":
      return Boolean((content.usecase || content.useCases)?.length > 0);
    case "Customer Stories": {
      const stories =
        content.customerstory?.stories ||
        content.stories ||
        content.customerStory?.stories;
      return Boolean(stories && stories.length > 0 && stories[0]?.url);
    }
    case "Resources":
      return Boolean(content.resources?.diagrams && content.resources.diagrams.length > 0);
    default:
      return true;
  }
};

/**
 * Dynamic Monitor Screen Positioning Hook
 * 
 * Replicates the browser's `background-size: cover` + `background-position: center right`
 * math to calculate the exact pixel position of the Apple Studio Display monitor screen
 * within the overview-studio-workstation.png background image.
 * 
 * This replaces 5 hardcoded CSS media query breakpoints + Firefox @supports hacks
 * with a single runtime calculation that works on ANY screen size and height.
 */
function Main() {
  const [selectedProduct, setSelectedProduct] = useState(() => {
    if (typeof window !== "undefined") {
      const p = window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase();
      if (p && productPathMap[p]) {
        return productPathMap[p];
      }
    }
    return "EngageAI";
  });
  const [selectedSection, setSelectedSection] = useState("Overview");
  const [activeSubnavTab, setActiveSubnavTab] = useState("Overview");
  const [heroOpacity, setHeroOpacity] = useState(1);
  const [isSubnavStuck, setIsSubnavStuck] = useState(false);
  const [isOverviewActive, setIsOverviewActive] = useState(false);
  const [isDemoActive, setIsDemoActive] = useState(false);
  const [isSwitchingProduct, setIsSwitchingProduct] = useState(false);
  const [assetsDrawerOpen, setAssetsDrawerOpen] = useState(false);

  // Pause Lenis when Assets Drawer is open
  useEffect(() => {
    if (lenisRef.current) {
      if (assetsDrawerOpen) {
        lenisRef.current.stop();
      } else {
        lenisRef.current.start();
      }
    }
  }, [assetsDrawerOpen]);

  const location = useLocation();
  const navigate = useNavigate();
  const postHeroRef = useRef(null);
  const isManualClickRef = useRef(false);
  const lenisRef = useRef(null);

  /* Production Apple Spatial Lenis Engine with Binary 2-State Wheel Lock */
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.5,
      prevent: (node) => {
        if (!node) return false;
        return (
          node.closest(".ant-drawer-content-wrapper") !== null ||
          node.closest(".ant-drawer-wrapper-body") !== null ||
          node.closest(".ant-drawer-body") !== null ||
          node.closest(".ant-modal-root") !== null ||
          node.closest(".partner-apple-assets-drawer") !== null ||
          node.closest(".partner-apple-account-modal") !== null ||
          node.closest(".modal-overlay") !== null ||
          node.closest(".preview-overlay") !== null ||
          node.closest(".modal-content") !== null ||
          node.closest(".preview-content") !== null ||
          node.closest(".preview-modal-body") !== null ||
          node.hasAttribute("data-lenis-prevent")
        );
      },
    });
    lenisRef.current = lenis;
    window.lenis = lenis;

    let isGliding = false;
    let glideTimeout = null;

    const handleWheel = (e) => {
      if (
        assetsDrawerOpen ||
        isGliding ||
        isManualClickRef.current ||
        document.body.style.overflow === "hidden" ||
        e.target.closest(".ant-drawer-content-wrapper") ||
        e.target.closest(".ant-drawer-wrapper-body") ||
        e.target.closest(".ant-drawer-body") ||
        e.target.closest(".ant-modal-root") ||
        e.target.closest(".ant-modal-content") ||
        e.target.closest(".partner-apple-account-modal")
      ) {
        return;
      }

      const scrollY = window.scrollY;
      const overviewEl = document.getElementById("section-overview");
      if (!overviewEl) return;

      const overviewY = overviewEl.getBoundingClientRect().top + scrollY - 20;

      // Cover 100% of the transitional pixel space between Hero (0px) and Overview (overviewY - 10px)
      if (scrollY < overviewY - 10) {
        if (e.deltaY > 0) {
          // Scrolling DOWN anywhere between 0px and Overview -> Glide 1-shot to Overview
          e.preventDefault();
          isGliding = true;
          isManualClickRef.current = true;
          lenis.scrollTo(overviewEl, { offset: -20, duration: 1.0 });

          clearTimeout(glideTimeout);
          glideTimeout = setTimeout(() => {
            isGliding = false;
            isManualClickRef.current = false;
          }, 1100);
        } else if (e.deltaY < 0) {
          // Scrolling UP anywhere between 0px and Overview -> Glide 1-shot back to Hero
          e.preventDefault();
          isGliding = true;
          isManualClickRef.current = true;
          lenis.scrollTo(0, { duration: 1.0 });

          clearTimeout(glideTimeout);
          glideTimeout = setTimeout(() => {
            isGliding = false;
            isManualClickRef.current = false;
          }, 1100);
        }
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      clearTimeout(glideTimeout);
      lenis.destroy();
    };
  }, [assetsDrawerOpen]);

  useEffect(() => {
    const p = location.pathname.split("/").filter(Boolean)[0]?.toLowerCase();
    if (p && productPathMap[p]) {
      const pName = productPathMap[p];
      if (selectedProduct !== pName) {
        setSelectedProduct(pName);
      }
      if (!checkSectionAvailable(pName, selectedSection)) {
        setSelectedSection("Overview");
        setActiveSubnavTab("Overview");
      }
    }
  }, [location, selectedSection, selectedProduct]);

  /* Scroll Listener for Hero Fade, Sticky Subnav Blur, Post-Hero BG Blur & ScrollSpy Sub-Nav Tracking */
  useEffect(() => {
    const sectionList = [
      { id: "section-overview", name: "Overview" },
      { id: "section-demos", name: "Demos" },
      { id: "section-pricing", name: "Pricing" },
      { id: "section-usecases", name: "Use Cases" },
      { id: "section-customer-stories", name: "Customer Stories" },
      { id: "section-resources", name: "Resources" },
    ];

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const opacity = Math.max(0, Math.min(1, 1 - scrollY / 350));
      setHeroOpacity(opacity);

      // Synchronize sticky header visibility directly with workstation monitor layer (isOverviewActive)
      const overviewEl = document.getElementById("section-overview");
      if (overviewEl) {
        const rect = overviewEl.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight * 0.7;
        setIsOverviewActive(isVisible);
        setIsSubnavStuck(isVisible);
      } else {
        setIsOverviewActive(false);
        setIsSubnavStuck(false);
      }

      // Detect when user reaches section-demos specifically (activates frosted blur)
      const demosEl = document.getElementById("section-demos");
      if (demosEl) {
        const rect = demosEl.getBoundingClientRect();
        setIsDemoActive(rect.top <= window.innerHeight * 0.7);
      } else {
        setIsDemoActive(false);
      }

      // ScrollSpy: Update active sub-nav tab highlight dynamically on scroll
      if (!isManualClickRef.current) {
        for (let i = sectionList.length - 1; i >= 0; i--) {
          const sec = sectionList[i];
          const el = document.getElementById(sec.id);
          if (el) {
            const rect = el.getBoundingClientRect();
            // Active tab changes when section top reaches near sticky header (~180px)
            if (rect.top <= 180 && rect.bottom >= 100) {
              if (checkSectionAvailable(selectedProduct, sec.name)) {
                setActiveSubnavTab(sec.name);
                break;
              }
            }
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [selectedProduct]);

  const scrollToProductOverview = () => {
    isManualClickRef.current = true;
    const overviewEl = document.getElementById("section-overview");
    if (overviewEl && lenisRef.current) {
      lenisRef.current.scrollTo(overviewEl, {
        offset: -20,
        duration: 1.2,
      });
    } else if (postHeroRef.current && lenisRef.current) {
      lenisRef.current.scrollTo(postHeroRef.current, {
        offset: -20,
        duration: 1.2,
      });
    } else if (overviewEl) {
      overviewEl.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (postHeroRef.current) {
      postHeroRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    setTimeout(() => {
      isManualClickRef.current = false;
    }, 1200);
  };

  const handleProductSelect = (tabName, shouldScroll = false) => {
    if (isSwitchingProduct) return;

    if (tabName !== selectedProduct) {
      // Trigger Apple Spatial Blur & Scale-Out Transition
      setIsSwitchingProduct(true);

      setTimeout(() => {
        setSelectedProduct(tabName);
        if (!checkSectionAvailable(tabName, selectedSection)) {
          setSelectedSection("Overview");
          setActiveSubnavTab("Overview");
        }
        const pathKey = Object.keys(productPathMap).find(
          (key) => productPathMap[key] === tabName
        );
        if (pathKey) {
          navigate(`/${pathKey}`);
        }

        // Smooth Spatial Reveal & Blur-Clear
        setTimeout(() => {
          setIsSwitchingProduct(false);
        }, 50);
      }, 220);
    }

    if (shouldScroll) {
      scrollToProductOverview();
    }
  };

  const handleSectionSelect = (secName) => {
    if (!checkSectionAvailable(selectedProduct, secName)) return;

    setActiveSubnavTab(secName);
    isManualClickRef.current = true;

    const targetId = sectionIdMap[secName];
    const targetEl = targetId ? document.getElementById(targetId) : null;
    if (targetEl && lenisRef.current) {
      lenisRef.current.scrollTo(targetEl, {
        offset: -20,
        duration: 1.2,
      });
    } else if (postHeroRef.current && lenisRef.current) {
      lenisRef.current.scrollTo(postHeroRef.current, {
        offset: -20,
        duration: 1.2,
      });
    } else if (targetEl) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    setTimeout(() => {
      isManualClickRef.current = false;
    }, 1200);
  };

  const renderProductContent = () => {
    switch (selectedProduct) {
      case "DocsAI":
        return <DocsAI selectedSection="Overview" />;
      case "InsightsAI":
        return <InsightsAI selectedSection="Overview" />;
      case "EngageAI":
        return <EngageAI selectedSection="Overview" />;
      case "WhatsApp":
        return <WhatsApp selectedSection="Overview" />;
      default:
        return <EngageAI selectedSection="Overview" />;
    }
  };

  const product3DScreenMap = {
    EngageAI: "/images/products/EngageAI-3D-Studio-Banner.png",
    DocsAI: "/images/products/DocsAI-3D-Studio-Banner.png",
    InsightsAI: "/images/products/InsightsAI-3D-Studio-Banner.png",
    WhatsApp: "/images/products/WhatsApp-3D-Studio-Banner.png",
  };

  return (
    <div
      className={`partner-main-layout ${
        isOverviewActive ? "is-overview-active" : ""
      } ${isDemoActive ? "is-demo-active" : ""}`}
    >
      {/* Outer Fixed Workstation Monitor Layer (Active only in Overview, Fades out in Demos) */}
      <div
        className={`fixed-workstation-monitor-layer ${
          isOverviewActive && !isDemoActive ? "is-visible" : ""
        }`}
      >
        <div className="fixed-workstation-monitor-stage">
          <div className="workstation-canvas-box">
            {/* Workstation Desk Base Image */}
            <img
              src="/images/banners/overview-studio-workstation.png"
              alt="Studio Workstation Desk"
              className="workstation-base-bg"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />

            {/* Black mask behind screen content */}
            <div className="monitor-black-mask" />

            {/* 3D Product Display Screens — Preloaded dedicated layers for instant, glitch-free switching */}
            {Object.entries(product3DScreenMap).map(([prodKey, imgSrc]) => {
              const isActive = selectedProduct === prodKey;
              return (
                <div
                  key={prodKey}
                  className={`fixed-workstation-monitor-img ${
                    isActive ? "is-active" : ""
                  } ${isSwitchingProduct ? "is-switching" : ""}`}
                >
                  <img
                    src={imgSrc}
                    alt={`${prodKey} Monitor Display`}
                    loading="eager"
                    fetchPriority={isActive ? "high" : "low"}
                    decoding="async"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Header Navbar with Assets Drawer Trigger */}
      <Header
        selectedProduct={selectedProduct}
        onOpenAssets={() => setAssetsDrawerOpen(true)}
        onSelectProduct={handleProductSelect}
      />

      {/* Stage 1: Hero Stage (Mandatory 100vh Snap Stage) */}
      <div
        className="partner-hero-stage"
        style={{
          opacity: heroOpacity,
          transform: `translateY(${ (1 - heroOpacity) * -20 }px)`,
          pointerEvents: heroOpacity > 0.1 ? "auto" : "none",
        }}
      >
        <Banner />
      </div>

      {/* Stage 2: Content View Stage (Mandatory 100vh Snap Stage) */}
      <div ref={postHeroRef} className="partner-post-hero-bg-wrapper">
        <div className="partner-content-container">
          {/* Apple Spatial Sticky Sub-Nav Header (Fades in ONLY when Overview reaches top 20%) */}
          <div
            className={`partner-apple-sticky-subnav ${
              isSubnavStuck ? "is-stuck" : ""
            }`}
          >
            <div className="partner-apple-subnav-inner">
              {/* Left Cluster: Product Selection Tabs */}
              <div className="partner-apple-product-group">
                {productTabs.map((tab) => {
                  const IconComponent = tab.icon;
                  const isActive = selectedProduct === tab.name;
                  return (
                    <button
                      key={tab.name}
                      type="button"
                      className={`partner-apple-product-tab ${
                        isActive ? "active" : ""
                      }`}
                      onClick={() => handleProductSelect(tab.name)}
                    >
                      <IconComponent size={16} className="partner-apple-tab-icon" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </div>

              {/* Right Cluster: Sub-Navigation Section Links (Dynamically renders ONLY valid sections for the active product) */}
              <div className="partner-apple-section-group">
                {sectionTabs
                  .filter((sec) => checkSectionAvailable(selectedProduct, sec))
                  .map((sec) => {
                    const isActive = activeSubnavTab === sec;
                    return (
                      <button
                        key={sec}
                        type="button"
                        className={`partner-apple-sec-tab ${
                          isActive ? "active" : ""
                        }`}
                        onClick={() => handleSectionSelect(sec)}
                      >
                        <span>{sec}</span>
                        {isActive && (
                          <div className="partner-apple-sec-dot" />
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Product Component View (Stacked Overview Mode with ScrollSpy active tabs) */}
          <div
            className={`partner-product-view ${
              isSwitchingProduct ? "is-switching" : ""
            }`}
          >
            {renderProductContent()}
          </div>
        </div>
      </div>

      {/* Ant Design Assets Drawer Overlay */}
      <Drawer
        zIndex={99999}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', Inter, sans-serif", fontWeight: 700, fontSize: "18px", color: "#1d1d1f" }}>
            <Briefcase size={20} color="#F05742" />
            <span>Partner Marketing & Technical Assets</span>
          </div>
        }
        closeIcon={
          <div className="apple-drawer-close-pill" aria-label="Close Drawer">
            <X size={18} />
          </div>
        }
        placement="right"
        onClose={() => setAssetsDrawerOpen(false)}
        open={assetsDrawerOpen}
        visible={assetsDrawerOpen}
        width={window.innerWidth < 768 ? "100%" : 580}
        className="partner-apple-assets-drawer"
        data-lenis-prevent="true"
        data-lenis-prevent-touch="true"
      >
        <Assets />
      </Drawer>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Main;
